/**
 * Pre-Departure Configuration - Vessel-specific checks and labels
 */

// Get vessel-specific checks based on type and features
export const getVesselChecks = (vesselType, currentVessel, voyageDetails) => {
  const baseChecks = {
    navigationLights: false,
    steering: false,
    engineTest: false,
    safetyEquipment: false,
    fuelChecked: false
  };

  // Inland waterway vessels - minimal
  if (vesselType === 'inland') {
    return {
      ...baseChecks,
      crewBriefed: false
    };
  }

  // Coastal workboats - standard WBC3
  if (vesselType === 'coastal') {
    return {
      ...baseChecks,
      sternGland: false,
      crewBriefed: false,
      weatherChecked: false,
      radioCheck: false,
      // Passenger-specific checks
      ...(voyageDetails?.hasPassengers && {
        passengerBriefing: false,
        passengerCount: false,
        emergencyDemo: false,
        lifejacketsShown: false
      }),
      // Additional for specific vessel features
      ...(currentVessel?.hasHydraulics && { hydraulics: false }),
      ...(currentVessel?.hasCrane && { craneSecured: false }),
      ...(currentVessel?.hasTowingGear && { towingGear: false })
    };
  }

  // Commercial vessels - comprehensive
  if (vesselType === 'commercial') {
    return {
      ...baseChecks,
      sternGland: false,
      crewBriefed: false,
      weatherChecked: false,
      radioCheck: false,
      logbook: false,
      chartsUpdated: false,
      passagePlan: false,
      // Passenger-specific checks (more comprehensive for commercial)
      ...(voyageDetails?.hasPassengers && {
        passengerBriefing: false,
        passengerCount: false,
        passengerManifest: false,
        emergencyDemo: false,
        lifejacketsShown: false,
        musterStations: false,
        emergencySignals: false
      }),
      // Additional for larger vessels
      ...(currentVessel?.hasWatertightDoors && { watertightDoors: false }),
      ...(currentVessel?.hasBilgeAlarms && { bilgeAlarms: false }),
      ...(currentVessel?.hasFireSystems && { fireSystems: false })
    };
  }

  return baseChecks;
};

// Check labels for display
export const getCheckLabels = () => ({
  navigationLights: 'Navigation lights tested',
  steering: 'Steering tested (hard to port/starboard)',
  engineTest: 'Engine tested ahead/astern',
  safetyEquipment: 'Safety equipment verified',
  fuelChecked: 'Fuel and water levels checked',
  sternGland: 'Stern gland checked for leaks',
  crewBriefed: 'Crew safety briefing completed',
  weatherChecked: 'Weather checked and acceptable',
  radioCheck: 'Radio equipment tested',
  // Passenger-specific
  passengerBriefing: 'Passenger safety briefing completed',
  passengerCount: 'Passenger count verified',
  passengerManifest: 'Passenger manifest prepared',
  emergencyDemo: 'Emergency procedures demonstrated',
  lifejacketsShown: 'Lifejacket locations shown',
  musterStations: 'Muster stations explained',
  emergencySignals: 'Emergency signals explained (7 short, 1 long)',
  // Vessel equipment
  hydraulics: 'Hydraulics checked and operational',
  craneSecured: 'Crane secured for sea',
  towingGear: 'Towing gear secured',
  logbook: 'Official logbook prepared',
  chartsUpdated: 'Charts updated and corrected',
  passagePlan: 'Passage plan reviewed',
  watertightDoors: 'Watertight doors closed and secured',
  bilgeAlarms: 'Bilge alarms tested',
  fireSystems: 'Fire detection/suppression systems checked'
});

// Required checks based on vessel type
export const getRequiredChecks = (vesselType, safetyChecks) => {
  if (vesselType === 'inland') {
    // Minimal for inland - just the essentials
    return ['navigationLights', 'steering', 'engineTest', 'safetyEquipment', 'fuelChecked'];
  }
  // All checks for coastal/commercial except optional ones
  return Object.keys(safetyChecks);
};